<script setup>
import { computed, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import PrevNextButtons from "@/components/PrevNextButtons.vue";
import ReferenceTabNavigation from "@/components/reference/ReferenceTabNavigation.vue";
import apiClient from "@/apiClient.js";
import { apiUrl } from "@/vars";
import dayjs from "dayjs";
import HistoricalVatImportModal from "@/views/Finance/HistoricalVatImportModal.vue";
import VatReturnStatistics from "@/views/Finance/VatReturnStatistics.vue";
import SupplierCreateModal from "@/views/Supplier/SupplierCreateModal.vue";
import EarlierOutgoingTransactionsModal from "@/views/Finance/EarlierOutgoingTransactionsModal.vue";
import IncomeTransactionReviewModal from "@/views/Finance/IncomeTransactionReviewModal.vue";
import { useAuthStore } from "@/stores/AuthStore";
import { incomeTransactionMatchesInvoiceFilter } from "@/utils/vatReturnIncomeFilter";
import {
  currentVatQuarter,
  readSelectedVatReturnPeriod,
  rememberSelectedVatReturnPeriod,
} from "@/utils/vatReturnPeriod";
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  EyeIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  TrashIcon,
} from "@heroicons/vue/24/solid";

const route = useRoute();
const router = useRouter();
const tabs = [
  { id: "overview", label: "Overview" },
  { id: "statistics", label: "Quarterly statistics" },
  { id: "outgoing", label: "Outgoing transactions" },
  { id: "evidence", label: "Invoice evidence" },
  { id: "vat-sheet", label: "VAT sheet" },
  { id: "income", label: "Income" },
];
const activeTab = computed(() =>
  tabs.some((tab) => tab.id === route.query.tab) ? route.query.tab : "overview",
);
const activeTabLabel = computed(
  () => tabs.find((tab) => tab.id === activeTab.value)?.label,
);
const selectTab = (tab) => router.push({ query: { ...route.query, tab } });

const defaultPeriod = currentVatQuarter();
const auth = useAuthStore();
const selectedPeriod =
  typeof window === "undefined"
    ? null
    : readSelectedVatReturnPeriod(window.localStorage);
const periodStart = ref(
  selectedPeriod?.period_start ?? defaultPeriod.period_start,
);
const periodEnd = ref(selectedPeriod?.period_end ?? defaultPeriod.period_end);
const quarterYear = ref(dayjs(periodStart.value).year());
const quarterNumber = ref(
  String(Math.floor(dayjs(periodStart.value).month() / 3) + 1),
);
const customPeriod = ref(
  currentVatQuarter(periodStart.value).period_start !== periodStart.value ||
    currentVatQuarter(periodStart.value).period_end !== periodEnd.value,
);
const changingStatus = ref(false);
const closureChecked = ref(false);
const applyQuarter = async () => {
  if (
    customPeriod.value ||
    !Number.isInteger(Number(quarterYear.value)) ||
    quarterYear.value < 2000 ||
    quarterYear.value > 2100
  )
    return;
  const period = currentVatQuarter(
    `${quarterYear.value}-${String(
      (Number(quarterNumber.value) - 1) * 3 + 1,
    ).padStart(2, "0")}-01`,
  );
  periodStart.value = period.period_start;
  periodEnd.value = period.period_end;
  await loadWorkspace();
};
const openStatisticsReturn = async (period) => {
  periodStart.value = period.period_start;
  periodEnd.value = period.period_end;
  quarterYear.value = dayjs(periodStart.value).year();
  quarterNumber.value = String(
    Math.floor(dayjs(periodStart.value).month() / 3) + 1,
  );
  customPeriod.value =
    currentVatQuarter(periodStart.value).period_start !== periodStart.value ||
    currentVatQuarter(periodStart.value).period_end !== periodEnd.value;
  if (period.vat_return?.id) {
    await openSavedReturn(period.vat_return);
  } else {
    await loadWorkspace();
  }
  await selectTab("overview");
};
const changeReturnStatus = async (status, ignoredInvoices = []) => {
  if (
    changingStatus.value ||
    !vatReturn.value ||
    vatReturn.value.period_start !== periodStart.value ||
    vatReturn.value.period_end !== periodEnd.value ||
    loading.value ||
    ((status === "locked" || status === "submitted") &&
      !closureChecked.value &&
      !vatReturn.value.filed_import)
  )
    return;
  changingStatus.value = true;
  error.value = "";
  allocationConflicts.value = [];
  selectedInvoiceConflictKeys.value = [];
  pendingFinalisationStatus.value = ["locked", "submitted"].includes(status)
    ? status
    : null;
  try {
    const { data } = await apiClient.put(
      `/api/vat-returns/${vatReturn.value.id}`,
      {
        status,
        ...(ignoredInvoices.length
          ? { ignore_invoice_conflicts: ignoredInvoices }
          : {}),
      },
      { suppressErrorAlert: true },
    );
    workspace.value = data;
    closureChecked.value = false;
    pendingFinalisationStatus.value = null;
  } catch (failure) {
    allocationConflicts.value =
      failure?.response?.data?.allocation_conflicts ?? [];
    error.value =
      failure?.response?.data?.message ?? "Unable to change VAT return status.";
  } finally {
    changingStatus.value = false;
  }
};
const loading = ref(false);
const error = ref("");
const overlappingReturns = ref([]);
const allocationConflicts = ref([]);
const selectedInvoiceConflictKeys = ref([]);
const pendingFinalisationStatus = ref(null);
const isInvoiceConflict = (issue) =>
  ["invoice_id", "purchase_invoice_id"].includes(issue.record_type);
const retryIgnoringInvoiceConflicts = async () => {
  if (!pendingFinalisationStatus.value || changingStatus.value) return;
  const selected = allocationConflicts.value.filter(
    (issue) =>
      isInvoiceConflict(issue) &&
      selectedInvoiceConflictKeys.value.includes(
        `${issue.record_type}:${issue.record_id}`,
      ),
  );
  if (!selected.length) return;
  await changeReturnStatus(
    pendingFinalisationStatus.value,
    selected.map((issue) => ({
      record_type: issue.record_type,
      record_id: issue.record_id,
    })),
  );
};
const workspace = ref(null);
const showEarlierOutgoing = ref(false);
const showHistoricalImport = ref(false);
const removingEarlierTransactionId = ref(null);
const selectedSupplier = ref(null);
const supplierInputKey = ref(0);
const activePurchaseInvoice = ref(null);
const activeTransaction = ref(null);
const transactionSupplier = ref(null);
const transactionSupplierInputKey = ref(0);
const supplierCreateTarget = ref(null);
const supplierCreateCompany = ref("");
const savingInvoice = ref(false);
const matching = ref(false);
const automating = ref(false);
const automationMessage = ref("");
const savingTransaction = ref(false);
const invoiceFile = ref(null);
const transactionSearch = ref("");
const incomeInvoiceFilter = ref("all");
const outgoingTransactionSearch = ref("");
const showLinkedSupplierTransactions = ref(true);
const selectedTransactionIds = ref([]);
const outgoingPerPage = ref(25);
const outgoingPage = ref(1);
const resolvedOutgoingPage = ref(1);
const outgoingPerPageOptions = [25, 50, 100];
const bulkSupplier = ref(null);
const bulkSupplierInputKey = ref(0);
const outgoingPaidOutSortActive = ref(false);
const outgoingPaidOutSortDirection = ref("DESC");
const bulkAssigning = ref(false);
const downloadingWorkbook = ref(false);
const activeIncomeTransaction = ref(null);
const incomeAllocations = ref([]);
const incomeAllocationError = ref("");
const incomeAllocationTotal = computed(() =>
  incomeAllocations.value.reduce(
    (sum, row) => sum + Math.round(Number(row.amount || 0) * 100),
    0,
  ),
);
const incomeAllocationRemaining = computed(
  () =>
    (activeIncomeTransaction.value?.paid_in ?? 0) - incomeAllocationTotal.value,
);
const validIncomeAllocations = computed(
  () =>
    incomeAllocations.value.length > 0 &&
    incomeAllocations.value.every(
      (row) =>
        /^\d+(?:\.\d{1,2})?$/.test(String(row.amount)) &&
        Number(row.amount) > 0 &&
        Math.round(Number(row.amount) * 100) <= row.invoice.total_amount,
    ) &&
    incomeAllocationRemaining.value >= 0,
);
const salesInvoiceInputKey = ref(0);
const savingIncomeInvoice = ref(false);
const activeIncomeReviewTransaction = ref(null);
const savingIncomeReview = ref(false);
const reviewingOutgoing = ref(false);
const bulkReviewStatus = ref("");
const rescanningInvoiceIds = ref([]);
const deletingInvoiceId = ref(null);
const rescanPollTimers = new Map();
const rescanPollAttempts = new Map();

const invoiceForm = reactive({
  supplier_id: "",
  invoice_date: "",
  due_date: "",
  invoice_number: "",
  description: "",
  gross_amount: "",
  net_amount: "",
  vat_amount: "",
  vat_treatment: "needs_review",
  category: "",
  analysis: "",
  notes: "",
});

const matchForm = reactive({
  purchase_invoice_id: "",
  bank_transaction_id: "",
  allocated_amount: "",
});

const vatTreatmentOptions = [
  { value: "standard_20", label: "20% VAT" },
  { value: "no_vat", label: "No VAT" },
  { value: "zero_rated", label: "Zero rated" },
  { value: "exempt", label: "Exempt" },
  { value: "reverse_charge", label: "Reverse charge" },
  { value: "needs_review", label: "Needs review" },
];

const outgoingReviewOptions = [
  { value: "duplicate", label: "Duplicate" },
  { value: "no_vat", label: "No VAT" },
  { value: "payroll", label: "Payroll" },
  { value: "bank_transfer", label: "Bank transfer" },
  { value: "tax_payment", label: "Tax payment" },
  { value: "out_of_scope", label: "Out of scope" },
  { value: "other", label: "Other" },
];

const vatReturn = computed(() => workspace.value?.vat_return ?? null);
const canCreateSuppliers = computed(() =>
  auth.hasAnyRole(["finance-department", "super"]),
);
const summary = computed(() => workspace.value?.summary ?? {});
const purchaseInvoices = computed(
  () => workspace.value?.purchase_invoices ?? [],
);
const unmatchedPurchaseInvoices = computed(
  () => workspace.value?.unmatched_purchase_invoices ?? [],
);
const unmatchedTransactions = computed(
  () => workspace.value?.unmatched_transactions ?? [],
);
const resolvedOutgoingTransactions = computed(
  () => workspace.value?.resolved_outgoing_transactions ?? [],
);
const incomeTransactions = computed(
  () => workspace.value?.income_transactions ?? [],
);
const stripeIncomeTransactions = computed(
  () => workspace.value?.stripe_income_transactions ?? [],
);
const vatItems = computed(() => workspace.value?.vat_items ?? []);
const normalizedTransactionSearch = computed(() =>
  transactionSearch.value.trim().toLowerCase(),
);
const normalizedOutgoingTransactionSearch = computed(() =>
  outgoingTransactionSearch.value.trim().toLowerCase(),
);
const transactionMatchesKeyword = (transaction) => {
  if (!normalizedTransactionSearch.value) {
    return true;
  }

  const haystack = [
    transaction.description,
    transaction.type,
    transaction.supplier?.company,
    ...(transaction.invoices ?? []).map((invoice) =>
      invoiceLabel(invoice, { includeAmount: false }),
    ),
    ...(transaction.suggested_invoices ?? []).map((invoice) =>
      invoiceLabel(invoice, { includeAmount: false }),
    ),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return haystack.includes(normalizedTransactionSearch.value);
};
const outgoingTransactionMatchesDescription = (transaction) => {
  if (!normalizedOutgoingTransactionSearch.value) {
    return true;
  }

  return String(transaction.description ?? "")
    .toLowerCase()
    .includes(normalizedOutgoingTransactionSearch.value);
};
const outgoingTransactionMatchesSupplierVisibility = (transaction) => {
  return showLinkedSupplierTransactions.value || !transaction.supplier?.id;
};
const filteredUnmatchedTransactions = computed(() => {
  return unmatchedTransactions.value
    .filter(transactionMatchesKeyword)
    .filter(outgoingTransactionMatchesDescription)
    .filter(outgoingTransactionMatchesSupplierVisibility);
});
const sortedOutgoingTransactions = computed(() => {
  const rows = [...filteredUnmatchedTransactions.value];

  if (!outgoingPaidOutSortActive.value) {
    return rows;
  }

  const direction = outgoingPaidOutSortDirection.value === "ASC" ? 1 : -1;

  return rows.sort((left, right) => {
    const leftPaidOut = Number(left?.paid_out ?? 0);
    const rightPaidOut = Number(right?.paid_out ?? 0);

    if (leftPaidOut === rightPaidOut) {
      return 0;
    }

    return leftPaidOut > rightPaidOut ? direction : -direction;
  });
});
const outgoingPageCount = computed(() =>
  Math.max(
    1,
    Math.ceil(sortedOutgoingTransactions.value.length / outgoingPerPage.value),
  ),
);
const resolvedOutgoingPageCount = computed(() =>
  Math.max(
    1,
    Math.ceil(
      resolvedOutgoingTransactions.value.length / outgoingPerPage.value,
    ),
  ),
);
const paginatedOutgoingTransactions = computed(() =>
  sortedOutgoingTransactions.value.slice(
    (outgoingPage.value - 1) * outgoingPerPage.value,
    outgoingPage.value * outgoingPerPage.value,
  ),
);
const paginatedResolvedOutgoingTransactions = computed(() =>
  resolvedOutgoingTransactions.value.slice(
    (resolvedOutgoingPage.value - 1) * outgoingPerPage.value,
    resolvedOutgoingPage.value * outgoingPerPage.value,
  ),
);
const outgoingPageStart = computed(() =>
  sortedOutgoingTransactions.value.length
    ? (outgoingPage.value - 1) * outgoingPerPage.value + 1
    : 0,
);
const outgoingPageEnd = computed(() =>
  Math.min(
    outgoingPage.value * outgoingPerPage.value,
    sortedOutgoingTransactions.value.length,
  ),
);

watch(
  [
    normalizedTransactionSearch,
    normalizedOutgoingTransactionSearch,
    showLinkedSupplierTransactions,
    outgoingPaidOutSortActive,
    outgoingPaidOutSortDirection,
    outgoingPerPage,
  ],
  () => {
    outgoingPage.value = 1;
  },
);
watch(outgoingPerPage, () => {
  resolvedOutgoingPage.value = 1;
});
watch(
  () => vatReturn.value?.id,
  () => {
    outgoingPage.value = 1;
    resolvedOutgoingPage.value = 1;
  },
);
watch(outgoingPageCount, (count) => {
  outgoingPage.value = Math.min(outgoingPage.value, count);
});
watch(resolvedOutgoingPageCount, (count) => {
  resolvedOutgoingPage.value = Math.min(resolvedOutgoingPage.value, count);
});

const filteredIncomeTransactions = computed(() =>
  incomeTransactions.value
    .filter(transactionMatchesKeyword)
    .filter((transaction) =>
      incomeTransactionMatchesInvoiceFilter(
        transaction,
        incomeInvoiceFilter.value,
      ),
    ),
);
const visibleTransactionIds = computed(() =>
  paginatedOutgoingTransactions.value.map((transaction) =>
    Number(transaction.id),
  ),
);
const selectedTransactionCount = computed(
  () => selectedTransactionIds.value.length,
);
const selectedVisibleTransactionCount = computed(() => {
  const visible = new Set(visibleTransactionIds.value);
  return selectedTransactionIds.value.filter((id) => visible.has(Number(id)))
    .length;
});
const allVisibleTransactionsSelected = computed(
  () =>
    visibleTransactionIds.value.length > 0 &&
    selectedVisibleTransactionCount.value ===
      visibleTransactionIds.value.length,
);
const matchablePurchaseInvoices = computed(() => {
  const seen = new Set();

  return [...unmatchedPurchaseInvoices.value, ...purchaseInvoices.value]
    .filter((invoice) => {
      if (!invoice?.id || seen.has(invoice.id)) {
        return false;
      }
      seen.add(invoice.id);
      return (invoice.outstanding_amount ?? invoice.gross_amount ?? 0) > 0;
    })
    .sort((left, right) => {
      const leftDate = left.invoice_date || left.created_at || "";
      const rightDate = right.invoice_date || right.created_at || "";
      return String(rightDate).localeCompare(String(leftDate));
    });
});

const formatCurrency = (value) => {
  if (value === null || value === undefined) {
    return "£0.00";
  }
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
  }).format(Number(value) / 100);
};

const formatDate = (value) => {
  if (!value) {
    return "—";
  }
  return dayjs(value).format("DD MMM YYYY");
};

const moneyToInput = (value) => {
  if (value === null || value === undefined || Number(value) <= 0) {
    return "";
  }

  return (Number(value) / 100).toFixed(2);
};

const supplierOutput = (supplier) => supplier?.company ?? "";
const outgoingPaidOutSortIndicator = computed(() => {
  if (!outgoingPaidOutSortActive.value) {
    return "↕";
  }

  return outgoingPaidOutSortDirection.value === "ASC" ? "▲" : "▼";
});

const reviewStatusLabel = (status) =>
  outgoingReviewOptions.find((option) => option.value === status)?.label ??
  status;

const invoiceReference = (invoice) => {
  if (!invoice) {
    return "";
  }
  if (invoice.reference) {
    return invoice.reference;
  }
  const typePrefix = invoice.reference?.type?.prefix ?? invoice.reference_type;
  const identifier =
    invoice.reference?.identifier ?? invoice.reference_identifier;
  return typePrefix && identifier
    ? `${typePrefix}${identifier}`
    : `Invoice #${invoice.id}`;
};

const invoiceLabel = (invoice, options = {}) => {
  if (!invoice) {
    return "";
  }
  const includeAmount = options.includeAmount !== false;
  const parts = [
    invoiceReference(invoice),
    invoice.customer || invoice.reference?.customer?.company,
    invoice.reference_name || invoice.reference?.name,
  ].filter(Boolean);

  if (includeAmount) {
    parts.push(
      formatCurrency(
        invoice.total_amount ??
          Number(invoice.subtotal ?? 0) + Number(invoice.vat ?? 0),
      ),
    );
  }

  return parts.join(" · ");
};

const purchaseInvoiceLabel = (invoice) => {
  if (!invoice) {
    return "";
  }

  return [
    invoice.supplier?.company || "No supplier",
    invoice.invoice_number || invoice.description || `Evidence #${invoice.id}`,
    formatCurrency(invoice.outstanding_amount ?? invoice.gross_amount),
  ]
    .filter(Boolean)
    .join(" · ");
};

const purchaseInvoiceSource = (invoice) => {
  if (invoice?.source_ticket_id) {
    return `Ticket #${invoice.source_ticket_id}`;
  }

  return "VAT workspace";
};

const bankTransactionLabels = (invoice) => {
  const transactions = Array.isArray(invoice?.bank_transactions)
    ? invoice.bank_transactions
    : [];

  if (!transactions.length) {
    return ["Pending bank transaction"];
  }

  return transactions.map((transaction) =>
    [
      `#${transaction.id}`,
      formatDate(transaction.transaction_date),
      transaction.description,
      formatCurrency(transaction.paid_out ?? transaction.allocated_amount),
    ]
      .filter(Boolean)
      .join(" · "),
  );
};

const evidenceNeedsReview = (invoice) =>
  !invoice?.supplier?.id ||
  !invoice?.invoice_date ||
  Number(invoice?.gross_amount ?? 0) <= 0 ||
  invoice?.vat_treatment === "needs_review";

const invoiceExtractionPending = (invoice) =>
  ["queued", "processing"].includes(
    String(invoice?.extraction_status ?? "").toLowerCase(),
  );

const invoiceIsRescanning = (invoice) =>
  rescanningInvoiceIds.value.includes(Number(invoice?.id));

const setInvoiceRescanning = (invoiceId, rescanning) => {
  const id = Number(invoiceId);
  const ids = new Set(rescanningInvoiceIds.value.map(Number));

  if (rescanning) {
    ids.add(id);
  } else {
    ids.delete(id);
  }

  rescanningInvoiceIds.value = Array.from(ids);
};

const updateWorkspacePurchaseInvoice = (invoice) => {
  if (!workspace.value || !invoice?.id) {
    return;
  }

  ["purchase_invoices", "unmatched_purchase_invoices"].forEach((key) => {
    const invoices = workspace.value[key];
    if (!Array.isArray(invoices)) {
      return;
    }

    const index = invoices.findIndex(
      (candidate) => Number(candidate.id) === Number(invoice.id),
    );
    if (index >= 0) {
      invoices.splice(index, 1, { ...invoices[index], ...invoice });
    }
  });
};

const stopPurchaseInvoiceRescanPolling = (invoiceId) => {
  const id = Number(invoiceId);
  const timer = rescanPollTimers.get(id);
  if (timer) {
    window.clearTimeout(timer);
  }
  rescanPollTimers.delete(id);
  rescanPollAttempts.delete(id);
  setInvoiceRescanning(id, false);
};

const pollPurchaseInvoiceRescan = async (invoiceId) => {
  const id = Number(invoiceId);
  const attempt = (rescanPollAttempts.get(id) ?? 0) + 1;
  rescanPollAttempts.set(id, attempt);

  try {
    const { data } = await apiClient.get(`/api/purchase-invoices/${id}`, {
      suppressAutoReload: true,
      suppressErrorAlert: true,
    });
    updateWorkspacePurchaseInvoice(data);

    if (!invoiceExtractionPending(data)) {
      stopPurchaseInvoiceRescanPolling(id);
      if (String(data?.extraction_status).toLowerCase() === "failed") {
        alert(data?.extraction_error ?? "Unable to rescan invoice evidence.");
      } else if (Number(data?.gross_amount ?? 0) <= 0) {
        alert(
          "The rescan completed but could not find a gross amount. You can try again or enter it manually.",
        );
      }
      return;
    }
  } catch {
    // Retry temporary polling failures while the queued rescan continues.
  }

  if (attempt >= 60) {
    stopPurchaseInvoiceRescanPolling(id);
    alert(
      "The rescan is still processing. Use refresh to check it again shortly.",
    );
    return;
  }

  rescanPollTimers.set(
    id,
    window.setTimeout(() => pollPurchaseInvoiceRescan(id), 2000),
  );
};

const rescanPurchaseInvoice = async (invoice) => {
  if (!invoice?.id || !invoice?.has_file || invoiceIsRescanning(invoice)) {
    return;
  }

  const id = Number(invoice.id);
  setInvoiceRescanning(id, true);
  rescanPollAttempts.set(id, 0);

  try {
    const { data } = await apiClient.post(
      `/api/purchase-invoices/${id}/rescan`,
      {},
      {
        suppressAutoReload: true,
        suppressErrorAlert: true,
      },
    );
    updateWorkspacePurchaseInvoice(data);
    pollPurchaseInvoiceRescan(id);
  } catch (err) {
    stopPurchaseInvoiceRescanPolling(id);
    alert(err?.response?.data?.message ?? "Unable to rescan invoice evidence.");
  }
};

const deletePurchaseInvoice = async (invoice) => {
  if (!invoice?.id || deletingInvoiceId.value !== null) {
    return;
  }

  const id = Number(invoice.id);
  const label =
    invoice.invoice_number || invoice.description || `evidence #${id}`;
  const fileMessage = invoice.source_ticket_id
    ? "The original ticket attachment will be kept."
    : "The uploaded evidence file will also be deleted.";

  if (
    !window.confirm(
      `Delete ${label} from review evidence? ${fileMessage} This cannot be undone.`,
    )
  ) {
    return;
  }

  deletingInvoiceId.value = id;
  try {
    await apiClient.delete(`/api/purchase-invoices/${id}`, {
      suppressAutoReload: true,
      suppressErrorAlert: true,
    });
    stopPurchaseInvoiceRescanPolling(id);

    if (Number(activePurchaseInvoice.value?.id) === id) {
      resetInvoiceForm();
    }
    if (Number(matchForm.purchase_invoice_id) === id) {
      matchForm.purchase_invoice_id = "";
    }

    try {
      await refreshWorkspace();
    } catch {
      ["purchase_invoices", "unmatched_purchase_invoices"].forEach((key) => {
        if (Array.isArray(workspace.value?.[key])) {
          workspace.value[key] = workspace.value[key].filter(
            (candidate) => Number(candidate.id) !== id,
          );
        }
      });
      alert(
        "The evidence was deleted, but the workspace could not refresh. Reload the page to update its totals.",
      );
    }
  } catch (err) {
    alert(
      err?.response?.data?.message ??
        "Unable to delete purchase invoice evidence.",
    );
  } finally {
    deletingInvoiceId.value = null;
  }
};

const matchedIncomeInvoice = (transaction) => transaction.invoices?.[0] ?? null;
const suggestedIncomeInvoice = (transaction) =>
  transaction.suggested_invoices?.[0] ?? null;

const selectInvoiceSupplier = (supplier) => {
  selectedSupplier.value = supplier ?? null;
  invoiceForm.supplier_id = supplier?.id ?? "";
  if (supplier?.default_vat_treatment) {
    invoiceForm.vat_treatment = supplier.default_vat_treatment;
  } else if (supplier?.vat_number) {
    invoiceForm.vat_treatment = "standard_20";
  }
  if (supplier?.default_vat_category) {
    invoiceForm.category = supplier.default_vat_category;
  }
  if (supplier?.default_vat_analysis) {
    invoiceForm.analysis = supplier.default_vat_analysis;
  }
};

const clearInvoiceSupplier = () => {
  selectedSupplier.value = null;
  invoiceForm.supplier_id = "";
  supplierInputKey.value++;
};

const fillInvoiceForm = (invoice) => {
  activePurchaseInvoice.value = invoice ?? null;
  selectedSupplier.value = invoice?.supplier ?? null;
  Object.assign(invoiceForm, {
    supplier_id: invoice?.supplier_id ?? invoice?.supplier?.id ?? "",
    invoice_date: invoice?.invoice_date ?? "",
    due_date: invoice?.due_date ?? "",
    invoice_number: invoice?.invoice_number ?? "",
    description: invoice?.description ?? "",
    gross_amount: moneyToInput(invoice?.gross_amount),
    net_amount: moneyToInput(invoice?.net_amount),
    vat_amount: moneyToInput(invoice?.vat_amount),
    vat_treatment: invoice?.vat_treatment ?? "needs_review",
    category: invoice?.category ?? "",
    analysis: invoice?.analysis ?? "",
    notes: invoice?.notes ?? "",
  });
  invoiceFile.value = null;
  supplierInputKey.value++;
};

const editPurchaseInvoice = (invoice) => {
  fillInvoiceForm(invoice);
};

const cancelPurchaseInvoiceEdit = () => {
  resetInvoiceForm();
};

const selectPurchaseInvoiceForMatch = (invoice) => {
  matchForm.purchase_invoice_id = invoice?.id ?? "";
};

const selectTransactionSupplier = (supplier) => {
  transactionSupplier.value = supplier ?? null;
};

const selectBulkSupplier = (supplier) => {
  bulkSupplier.value = supplier ?? null;
};

const openSupplierCreateModal = (company, target) => {
  supplierCreateCompany.value = String(company ?? "").trim();
  supplierCreateTarget.value = target;
};

const closeSupplierCreateModal = () => {
  supplierCreateTarget.value = null;
  supplierCreateCompany.value = "";
};

const useCreatedSupplier = (supplier) => {
  if (supplierCreateTarget.value === "bulk") {
    selectBulkSupplier(supplier);
  } else if (supplierCreateTarget.value === "transaction") {
    selectTransactionSupplier(supplier);
  }

  closeSupplierCreateModal();
};

const selectSalesInvoice = (invoice) => {
  if (
    !invoice ||
    incomeAllocations.value.some((row) => row.invoice.id === invoice.id)
  )
    return;
  const amount = Math.min(
    Math.max(0, incomeAllocationRemaining.value),
    invoice.total_amount,
  );
  incomeAllocations.value.push({ invoice, amount: (amount / 100).toFixed(2) });
  salesInvoiceInputKey.value++;
  incomeAllocationError.value = "";
};

const clearBulkSupplier = () => {
  bulkSupplier.value = null;
  bulkSupplierInputKey.value++;
};

const toggleOutgoingPaidOutSort = () => {
  if (!outgoingPaidOutSortActive.value) {
    outgoingPaidOutSortActive.value = true;
    outgoingPaidOutSortDirection.value = "DESC";
    return;
  }

  outgoingPaidOutSortDirection.value =
    outgoingPaidOutSortDirection.value === "ASC" ? "DESC" : "ASC";
};

const syncSelectedTransactions = () => {
  const validIds = new Set(
    unmatchedTransactions.value.map((transaction) => Number(transaction.id)),
  );
  selectedTransactionIds.value = selectedTransactionIds.value
    .map((id) => Number(id))
    .filter((id) => validIds.has(id));
};

const toggleVisibleTransactions = () => {
  const selected = new Set(
    selectedTransactionIds.value.map((id) => Number(id)),
  );

  if (allVisibleTransactionsSelected.value) {
    visibleTransactionIds.value.forEach((id) => selected.delete(id));
  } else {
    visibleTransactionIds.value.forEach((id) => selected.add(id));
  }

  selectedTransactionIds.value = Array.from(selected);
};

const clearSelectedTransactions = () => {
  selectedTransactionIds.value = [];
};

const openIncomeInvoiceModal = (transaction) => {
  if (savingIncomeInvoice.value) return;
  activeIncomeTransaction.value = transaction;
  incomeAllocationError.value = "";
  incomeAllocations.value = (transaction.invoices ?? []).map((invoice) => ({
    invoice,
    amount: (
      (invoice.allocated_amount ??
        Math.min(transaction.paid_in, invoice.total_amount)) / 100
    ).toFixed(2),
  }));
  if (!incomeAllocations.value.length && suggestedIncomeInvoice(transaction)) {
    selectSalesInvoice(suggestedIncomeInvoice(transaction));
  }
  salesInvoiceInputKey.value++;
};

const openIncomeInvoiceFromRow = (event, transaction) => {
  if (event.target.closest("button, a, input, select, textarea, label")) return;
  openIncomeInvoiceModal(transaction);
};

const closeIncomeInvoiceModal = () => {
  activeIncomeTransaction.value = null;
  incomeAllocations.value = [];
};

const loadWorkspace = async () => {
  loading.value = true;
  error.value = "";
  overlappingReturns.value = [];
  allocationConflicts.value = [];
  selectedInvoiceConflictKeys.value = [];
  pendingFinalisationStatus.value = null;
  closureChecked.value = false;
  try {
    const { data } = await apiClient.post(
      "/api/vat-returns",
      {
        period_start: periodStart.value,
        period_end: periodEnd.value,
        label: `${dayjs(periodStart.value).format("D MMM YYYY")} - ${dayjs(
          periodEnd.value,
        ).format("D MMM YYYY")}`,
        scheme: "cash",
      },
      { suppressErrorAlert: true },
    );
    workspace.value = data;
    closureChecked.value = false;
    rememberSelectedVatReturnPeriod(window.localStorage, data.vat_return);
    syncSelectedTransactions();
  } catch (err) {
    workspace.value = null;
    syncSelectedTransactions();
    overlappingReturns.value = err?.response?.data?.overlapping_returns ?? [];
    error.value =
      err?.response?.data?.message ?? "Unable to load VAT return workspace.";
  } finally {
    loading.value = false;
  }
};

const openSavedReturn = async (savedReturn) => {
  if (loading.value) return;
  loading.value = true;
  error.value = "";
  allocationConflicts.value = [];
  selectedInvoiceConflictKeys.value = [];
  pendingFinalisationStatus.value = null;
  closureChecked.value = false;
  try {
    const { data } = await apiClient.get(`/api/vat-returns/${savedReturn.id}`, {
      suppressErrorAlert: true,
    });
    workspace.value = data;
    periodStart.value = data.vat_return.period_start;
    periodEnd.value = data.vat_return.period_end;
    quarterYear.value = dayjs(periodStart.value).year();
    quarterNumber.value = String(
      Math.floor(dayjs(periodStart.value).month() / 3) + 1,
    );
    customPeriod.value =
      currentVatQuarter(periodStart.value).period_start !== periodStart.value ||
      currentVatQuarter(periodStart.value).period_end !== periodEnd.value;
    overlappingReturns.value = [];
    rememberSelectedVatReturnPeriod(window.localStorage, data.vat_return);
    syncSelectedTransactions();
  } catch (failure) {
    workspace.value = null;
    error.value =
      failure?.response?.data?.message ??
      "Unable to open the saved VAT return.";
  } finally {
    loading.value = false;
  }
};

const useEarlierOutgoingTransactions = (data) => {
  workspace.value = data;
  showEarlierOutgoing.value = false;
  syncSelectedTransactions();
};

const removeEarlierTransaction = async (transaction) => {
  if (removingEarlierTransactionId.value) return;
  removingEarlierTransactionId.value = transaction.id;
  try {
    const { data } = await apiClient.delete(
      `/api/vat-returns/${vatReturn.value.id}/earlier-outgoing-transactions/${transaction.id}`,
      { suppressErrorAlert: true },
    );
    useEarlierOutgoingTransactions(data);
  } catch (failure) {
    error.value =
      failure?.response?.data?.message ??
      "Unable to remove earlier transaction.";
  } finally {
    removingEarlierTransactionId.value = null;
  }
};

const automateWorkspace = async () => {
  if (!vatReturn.value?.id) return;
  automating.value = true;
  automationMessage.value = "";
  try {
    const { data } = await apiClient.post(
      `/api/vat-returns/${vatReturn.value.id}/automate`,
    );
    workspace.value = data.workspace;
    syncSelectedTransactions();
    automationMessage.value = `${data.automation.supplier_matches} supplier matches and ${data.automation.invoice_matches} invoice matches applied. Ambiguous matches remain for review.`;
  } catch (err) {
    error.value = err?.response?.data?.message ?? "Unable to run VAT matching.";
  } finally {
    automating.value = false;
  }
};

const refreshWorkspace = async () => {
  if (!vatReturn.value?.id) {
    await loadWorkspace();
    return;
  }
  loading.value = true;
  error.value = "";
  allocationConflicts.value = [];
  selectedInvoiceConflictKeys.value = [];
  pendingFinalisationStatus.value = null;
  try {
    const { data } = await apiClient.get(
      `/api/vat-returns/${vatReturn.value.id}`,
    );
    workspace.value = data;
    syncSelectedTransactions();
  } finally {
    loading.value = false;
  }
};

const handleInvoiceFile = (event) => {
  invoiceFile.value = event.target.files?.[0] ?? null;
};

const resetInvoiceForm = () => {
  activePurchaseInvoice.value = null;
  Object.assign(invoiceForm, {
    supplier_id: "",
    invoice_date: "",
    due_date: "",
    invoice_number: "",
    description: "",
    gross_amount: "",
    net_amount: "",
    vat_amount: "",
    vat_treatment: "needs_review",
    category: "",
    analysis: "",
    notes: "",
  });
  invoiceFile.value = null;
  clearInvoiceSupplier();
};

const saveInvoice = async () => {
  if (!invoiceForm.gross_amount) {
    alert("Gross amount is required.");
    return;
  }

  savingInvoice.value = true;
  try {
    if (activePurchaseInvoice.value?.id) {
      const payload = {};
      Object.entries(invoiceForm).forEach(([key, value]) => {
        if (value !== "" && value !== undefined) {
          payload[key] = value;
        }
      });
      payload.supplier_id = selectedSupplier.value?.id ?? null;

      await apiClient.put(
        `/api/purchase-invoices/${activePurchaseInvoice.value.id}`,
        payload,
      );
    } else {
      const formData = new FormData();
      Object.entries(invoiceForm).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });
      if (invoiceFile.value) {
        formData.append("file", invoiceFile.value);
      }
      await apiClient.post("/api/purchase-invoices", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    }
    resetInvoiceForm();
    await refreshWorkspace();
  } catch (err) {
    alert(
      err?.response?.data?.message ??
        "Unable to save purchase invoice evidence.",
    );
  } finally {
    savingInvoice.value = false;
  }
};

const linkInvoiceToTransaction = async () => {
  if (!matchForm.purchase_invoice_id || !matchForm.bank_transaction_id) {
    alert("Select an invoice and a transaction to match.");
    return;
  }

  matching.value = true;
  try {
    await apiClient.post(
      `/api/purchase-invoices/${matchForm.purchase_invoice_id}/transactions`,
      {
        bank_transaction_id: matchForm.bank_transaction_id,
        allocated_amount: matchForm.allocated_amount || null,
      },
    );
    Object.assign(matchForm, {
      purchase_invoice_id: "",
      bank_transaction_id: "",
      allocated_amount: "",
    });
    await refreshWorkspace();
  } catch (err) {
    alert(
      err?.response?.data?.message ?? "Unable to link invoice and transaction.",
    );
  } finally {
    matching.value = false;
  }
};

const openTransactionSupplierModal = (transaction) => {
  activeTransaction.value = transaction;
  transactionSupplier.value = transaction.supplier ?? null;
  transactionSupplierInputKey.value++;
};

const closeTransactionSupplierModal = () => {
  activeTransaction.value = null;
  transactionSupplier.value = null;
};

const saveTransactionSupplier = async () => {
  if (!vatReturn.value?.id || !activeTransaction.value) {
    return;
  }

  savingTransaction.value = true;
  try {
    await apiClient.put(
      `/api/vat-returns/${vatReturn.value.id}/transactions/${activeTransaction.value.id}`,
      {
        supplier_id: transactionSupplier.value?.id ?? null,
      },
    );
    closeTransactionSupplierModal();
    await refreshWorkspace();
  } catch (err) {
    alert(
      err?.response?.data?.message ?? "Unable to update transaction supplier.",
    );
  } finally {
    savingTransaction.value = false;
  }
};

const bulkAssignSupplier = async () => {
  if (!vatReturn.value?.id) {
    return;
  }
  if (!bulkSupplier.value?.id) {
    alert("Select a supplier.");
    return;
  }
  if (!selectedTransactionIds.value.length) {
    alert("Select at least one transaction.");
    return;
  }

  bulkAssigning.value = true;
  try {
    await apiClient.put(
      `/api/vat-returns/${vatReturn.value.id}/transactions/bulk-supplier`,
      {
        supplier_id: bulkSupplier.value.id,
        transaction_ids: selectedTransactionIds.value.map((id) => Number(id)),
      },
    );
    clearSelectedTransactions();
    outgoingTransactionSearch.value = "";
    clearBulkSupplier();
    await refreshWorkspace();
  } catch (err) {
    alert(err?.response?.data?.message ?? "Unable to bulk assign supplier.");
  } finally {
    bulkAssigning.value = false;
  }
};

const markOutgoingTransactionReviewed = async (transaction, status) => {
  if (!vatReturn.value?.id || !transaction?.id || !status) {
    return;
  }

  reviewingOutgoing.value = true;
  try {
    await apiClient.put(
      `/api/vat-returns/${vatReturn.value.id}/outgoing-transactions/${transaction.id}/review`,
      { status },
    );
    await refreshWorkspace();
  } catch (err) {
    alert(
      err?.response?.data?.message ??
        "Unable to mark outgoing transaction as reviewed.",
    );
  } finally {
    reviewingOutgoing.value = false;
  }
};

const bulkReviewOutgoingTransactions = async () => {
  if (!vatReturn.value?.id) {
    return;
  }
  if (!bulkReviewStatus.value) {
    alert("Select a review outcome.");
    return;
  }
  if (!selectedTransactionIds.value.length) {
    alert("Select at least one transaction.");
    return;
  }

  reviewingOutgoing.value = true;
  try {
    await apiClient.put(
      `/api/vat-returns/${vatReturn.value.id}/transactions/bulk-review`,
      {
        status: bulkReviewStatus.value,
        transaction_ids: selectedTransactionIds.value.map((id) => Number(id)),
      },
    );
    bulkReviewStatus.value = "";
    clearSelectedTransactions();
    await refreshWorkspace();
  } catch (err) {
    alert(
      err?.response?.data?.message ??
        "Unable to mark outgoing transactions as reviewed.",
    );
  } finally {
    reviewingOutgoing.value = false;
  }
};

const reopenOutgoingTransaction = async (review) => {
  if (!vatReturn.value?.id || !review?.bank_transaction_id) {
    return;
  }

  reviewingOutgoing.value = true;
  try {
    await apiClient.delete(
      `/api/vat-returns/${vatReturn.value.id}/outgoing-transactions/${review.bank_transaction_id}/review`,
    );
    await refreshWorkspace();
  } catch (err) {
    alert(err?.response?.data?.message ?? "Unable to reopen transaction.");
  } finally {
    reviewingOutgoing.value = false;
  }
};

const saveIncomeInvoiceMatch = async (transaction, invoice) => {
  if (!vatReturn.value?.id || !transaction?.id || !invoice?.id) {
    return;
  }

  savingIncomeInvoice.value = true;
  try {
    await apiClient.put(
      `/api/vat-returns/${vatReturn.value.id}/income-transactions/${transaction.id}/invoice`,
      {
        invoice_id: invoice.id,
        replace_existing: true,
      },
    );
    closeIncomeInvoiceModal();
    await refreshWorkspace();
  } catch (err) {
    alert(err?.response?.data?.message ?? "Unable to match income invoice.");
  } finally {
    savingIncomeInvoice.value = false;
  }
};

const applySuggestedIncomeInvoice = async (transaction) => {
  const invoice = suggestedIncomeInvoice(transaction);
  if (!invoice) {
    return;
  }
  await saveIncomeInvoiceMatch(transaction, invoice);
};

const saveSelectedIncomeInvoice = async () => {
  if (!validIncomeAllocations.value || savingIncomeInvoice.value) return;
  savingIncomeInvoice.value = true;
  incomeAllocationError.value = "";
  try {
    await apiClient.put(
      `/api/vat-returns/${vatReturn.value.id}/income-transactions/${activeIncomeTransaction.value.id}/invoice`,
      {
        allocations: incomeAllocations.value.map((row) => ({
          invoice_id: row.invoice.id,
          allocated_amount: Number(row.amount).toFixed(2),
        })),
        replace_existing: true,
      },
    );
    closeIncomeInvoiceModal();
    await refreshWorkspace();
  } catch (err) {
    incomeAllocationError.value =
      err?.response?.data?.message ?? "Unable to save invoice allocations.";
  } finally {
    savingIncomeInvoice.value = false;
  }
};

const clearIncomeInvoiceMatch = async (transaction, invoice) => {
  if (!vatReturn.value?.id || !transaction?.id || !invoice?.id) {
    return;
  }

  savingIncomeInvoice.value = true;
  try {
    await apiClient.delete(
      `/api/vat-returns/${vatReturn.value.id}/income-transactions/${transaction.id}/invoice/${invoice.id}`,
    );
    await refreshWorkspace();
  } catch (err) {
    alert(err?.response?.data?.message ?? "Unable to clear invoice match.");
  } finally {
    savingIncomeInvoice.value = false;
  }
};

const openIncomeReviewModal = (transaction) => {
  activeIncomeReviewTransaction.value = transaction;
};

const closeIncomeReviewModal = () => {
  if (savingIncomeReview.value) {
    return;
  }
  activeIncomeReviewTransaction.value = null;
};

const saveIncomeTransactionReview = async (review) => {
  if (!vatReturn.value?.id || !activeIncomeReviewTransaction.value?.id) {
    return;
  }

  savingIncomeReview.value = true;
  try {
    await apiClient.put(
      `/api/vat-returns/${vatReturn.value.id}/income-transactions/${activeIncomeReviewTransaction.value.id}/review`,
      review,
    );
    activeIncomeReviewTransaction.value = null;
    await refreshWorkspace();
  } catch (err) {
    alert(err?.response?.data?.message ?? "Unable to save income review.");
  } finally {
    savingIncomeReview.value = false;
  }
};

const incomeReviewButtonLabel = (transaction) => {
  if (transaction.review) {
    return "Edit review";
  }
  return transaction.notes ? "Edit note" : "Review";
};

const filenameFromDisposition = (disposition, fallback) => {
  if (!disposition) {
    return fallback;
  }

  const encodedMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (encodedMatch?.[1]) {
    return decodeURIComponent(encodedMatch[1].replaceAll('"', ""));
  }

  const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
  return filenameMatch?.[1] ?? fallback;
};

const triggerDownload = (data, filename, type) => {
  const blob = data instanceof Blob ? data : new Blob([data], { type });
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
};

const downloadWorkbook = async () => {
  if (!vatReturn.value?.id) {
    return;
  }

  downloadingWorkbook.value = true;
  try {
    const response = await apiClient.get(
      `/api/vat-returns/${vatReturn.value.id}/workbook`,
      {
        responseType: "blob",
      },
    );
    const fallback = `VAT ${vatReturn.value.period_start} - ${vatReturn.value.period_end}.xlsx`;
    const filename = filenameFromDisposition(
      response.headers?.["content-disposition"],
      fallback,
    );
    triggerDownload(
      response.data,
      filename,
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    );
  } catch (err) {
    alert(err?.response?.data?.message ?? "Unable to download VAT workbook.");
  } finally {
    downloadingWorkbook.value = false;
  }
};

const downloadInvoiceUrl = (invoice) =>
  `${apiUrl}/purchase-invoices/${invoice.id}/download`;
const viewInvoiceUrl = (invoice) =>
  `${apiUrl}/purchase-invoices/${invoice.id}/download?inline=1`;

onMounted(loadWorkspace);
onUnmounted(() => {
  Array.from(rescanPollTimers.keys()).forEach(stopPurchaseInvoiceRescanPolling);
});
</script>

<template>
  <div class="vat-returns-workspace space-y-6">
    <form-box header="VAT Return Workspace">
      <div class="flex flex-col gap-4">
        <div
          class="grid gap-3 md:grid-cols-[180px_180px_minmax(220px,1fr)_auto] md:items-end"
        >
          <div class="flex flex-col gap-1">
            <label
              class="text-xs uppercase text-slate-500"
              for="vat-quarter-year"
              >Year</label
            >
            <input
              id="vat-quarter-year"
              v-model.number="quarterYear"
              type="number"
              min="2000"
              max="2100"
              class="input"
              :disabled="loading || changingStatus || customPeriod"
              @change="applyQuarter"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs uppercase text-slate-500" for="vat-quarter"
              >VAT quarter (calendar year)</label
            >
            <select
              id="vat-quarter"
              v-model="quarterNumber"
              class="input"
              :disabled="loading || changingStatus || customPeriod"
              @change="applyQuarter"
            >
              <option value="1">Q1 · January–March</option>
              <option value="2">Q2 · April–June</option>
              <option value="3">Q3 · July–September</option>
              <option value="4">Q4 · October–December</option>
            </select>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs uppercase text-slate-500">Keyword</label>
            <input
              v-model="transactionSearch"
              type="search"
              class="input dark:border-slate-300 dark:bg-slate-800"
              placeholder="Description or supplier"
            />
          </div>
          <div class="flex gap-2">
            <button
              class="btn accent px-3"
              :disabled="loading"
              @click.prevent="loadWorkspace"
            >
              <MagnifyingGlassIcon class="icon" />
            </button>
            <button
              class="btn px-3"
              :disabled="loading"
              @click.prevent="refreshWorkspace"
            >
              <ArrowPathIcon class="icon" />
            </button>
            <button
              v-if="vatReturn"
              class="btn px-3 inline-flex items-center"
              :disabled="downloadingWorkbook"
              @click.prevent="downloadWorkbook"
            >
              <ArrowDownTrayIcon class="icon" />
            </button>
          </div>
        </div>

        <div class="flex flex-wrap items-center gap-3 text-sm">
          <span>{{ periodStart }} – {{ periodEnd }}</span>
          <label
            ><input
              v-model="customPeriod"
              type="checkbox"
              @change="applyQuarter"
            />
            Custom dates</label
          >
          <template v-if="customPeriod">
            <label
              >Period start
              <input v-model="periodStart" type="date" class="input"
            /></label>
            <label
              >Period end <input v-model="periodEnd" type="date" class="input"
            /></label>
          </template>
        </div>
        <div
          v-if="
            vatReturn &&
            vatReturn.period_start === periodStart &&
            vatReturn.period_end === periodEnd
          "
          class="space-y-2 rounded border border-slate-200 p-3 dark:border-slate-700"
        >
          <p class="text-sm">
            Return {{ vatReturn.period_start }} – {{ vatReturn.period_end }} ·
            status: <strong>{{ vatReturn.status }}</strong
            >. Closing records the outgoing payments currently included.
            Reconcile historical periods against the filed books before closing.
            This does not submit a return to HMRC.
          </p>
          <template v-if="vatReturn.status !== 'submitted'">
            <p v-if="vatReturn.filed_import" class="text-sm">
              Workbook matches confirmed. Closing preserves the reserved
              payments and invoices; no row-by-row review is required.
            </p>
            <label v-else class="flex items-start gap-2 text-sm"
              ><input
                v-model="closureChecked"
                type="checkbox"
                :disabled="loading || changingStatus"
              />
              I checked the included payments and totals against the books and,
              for a historical return, the filed return.</label
            >
            <div class="flex gap-2">
              <button
                v-if="vatReturn.status !== 'locked'"
                type="button"
                class="btn"
                :disabled="
                  loading ||
                  changingStatus ||
                  (!closureChecked && !vatReturn.filed_import)
                "
                @click="changeReturnStatus('locked')"
              >
                Close / lock return
              </button>
              <button
                type="button"
                class="btn"
                :disabled="
                  loading ||
                  changingStatus ||
                  (!closureChecked && !vatReturn.filed_import)
                "
                @click="changeReturnStatus('submitted')"
              >
                Record as already submitted
              </button>
            </div>
          </template>
          <button
            v-if="['locked', 'submitted'].includes(vatReturn.status)"
            type="button"
            class="btn"
            :disabled="loading || changingStatus"
            @click="changeReturnStatus('draft')"
          >
            Reopen return
          </button>
        </div>
        <div
          v-if="vatReturn"
          class="space-y-3 rounded border border-slate-200 p-3 dark:border-slate-700"
        >
          <template v-if="vatReturn.filed_import">
            <p class="text-sm font-semibold">
              Historical workbook preserved:
              {{ vatReturn.filed_import.filename }}
            </p>
            <p class="text-sm">
              Filed input VAT
              {{ formatCurrency(vatReturn.filed_import.input_vat) }} · output
              VAT {{ formatCurrency(vatReturn.filed_import.output_vat) }} ·
              liability / repayment
              {{ formatCurrency(vatReturn.filed_import.liability) }}.
            </p>
            <p class="text-sm">
              {{ vatReturn.filed_import.reconciliation_notes }}
            </p>
            <p class="text-sm">
              The export downloads the original workbook. Workspace calculations
              below remain live estimates; quarterly statistics use the
              preserved filed totals.
            </p>
            <button
              type="button"
              class="btn"
              :disabled="loading"
              @click="showHistoricalImport = true"
            >
              View historical allocations
            </button>
          </template>
          <template v-else>
            <p class="text-sm">
              Reconcile a previously filed quarter by importing its Neptune VAT
              workbook before closing it.
            </p>
            <button
              type="button"
              class="btn"
              :disabled="
                loading || ['locked', 'submitted'].includes(vatReturn.status)
              "
              @click="showHistoricalImport = true"
            >
              Import previous VAT workbook
            </button>
          </template>
        </div>
        <div v-if="vatReturn" class="flex items-center gap-3">
          <button
            class="btn"
            :disabled="
              automating ||
              loading ||
              ['locked', 'submitted'].includes(vatReturn.status)
            "
            @click.prevent="automateWorkspace"
          >
            {{
              automating ? "Matching…" : "Match known suppliers and invoices"
            }}
          </button>
          <span v-if="automationMessage" class="text-sm">{{
            automationMessage
          }}</span>
        </div>

        <p v-if="error" class="text-sm text-rose-600" role="alert">
          {{ error }}
        </p>
        <div
          v-if="allocationConflicts.length"
          class="space-y-3 rounded border border-rose-400 p-3"
          aria-label="VAT allocation conflicts"
        >
          <p class="text-sm">
            The dates do not need to overlap for an invoice or payment to be
            shared. These records are already reserved elsewhere; this return
            has not been closed.
          </p>
          <p v-if="!vatReturn?.filed_import" class="text-sm">
            No historical workbook has been confirmed for this quarter. For a
            previously filed return, import the workbook sent to your accountant
            to check only its intended transactions.
          </p>
          <div
            v-for="issue in allocationConflicts"
            :key="`${issue.record_type}-${issue.record_id}`"
            class="space-y-1 text-sm"
          >
            <strong>{{ issue.label }}</strong>
            <label
              v-if="isInvoiceConflict(issue)"
              class="flex items-start gap-2"
            >
              <input
                v-model="selectedInvoiceConflictKeys"
                type="checkbox"
                :value="`${issue.record_type}:${issue.record_id}`"
                :disabled="changingStatus"
                :aria-label="`Ignore invoice conflict ${issue.record_id}`"
              />
              Ignore this invoice conflict for this quarter
            </label>
            <p>{{ issue.message }}</p>
            <ul
              v-if="issue.related_payments?.length"
              class="list-inside list-disc"
            >
              <li
                v-for="payment in issue.related_payments"
                :key="`${payment.source}-${payment.id}`"
              >
                {{ payment.source === "bank" ? "Bank" : "Stripe" }} #{{
                  payment.id
                }}
                · {{ formatDate(payment.date) }} · {{ payment.description }} ·
                {{ formatCurrency(payment.amount) }}
              </li>
            </ul>
          </div>
        </div>
        <div
          v-if="allocationConflicts.some(isInvoiceConflict)"
          class="space-y-2 rounded border border-amber-400 p-3 text-sm"
        >
          <p>
            You can ignore selected invoice conflicts and continue. The invoice
            keeps its original quarter ownership; this quarter retains its own
            payments and may use the invoice details. Your choice will be
            recorded. Bank and Stripe payment conflicts cannot be ignored.
          </p>
          <button
            type="button"
            class="btn"
            :disabled="
              changingStatus ||
              !selectedInvoiceConflictKeys.length ||
              !pendingFinalisationStatus
            "
            @click="retryIgnoringInvoiceConflicts"
          >
            Ignore selected invoice conflicts and
            {{
              pendingFinalisationStatus === "submitted"
                ? "record as submitted"
                : "close quarter"
            }}
          </button>
        </div>
        <div
          v-if="vatReturn?.ignored_invoice_conflicts?.length"
          class="rounded border border-amber-400 p-3 text-sm"
        >
          <p>Invoice conflicts explicitly ignored for this quarter:</p>
          <ul class="list-inside list-disc">
            <li
              v-for="item in vatReturn.ignored_invoice_conflicts"
              :key="`${item.record_type}-${item.record_id}`"
            >
              {{ item.label }} · acknowledged
              {{ formatDate(item.acknowledged_at) }}
            </li>
          </ul>
        </div>
        <div v-if="overlappingReturns.length" class="space-y-2">
          <p class="text-sm">Saved returns blocking this new period:</p>
          <button
            v-for="savedReturn in overlappingReturns"
            :key="savedReturn.id"
            type="button"
            class="btn block"
            :disabled="loading"
            @click="openSavedReturn(savedReturn)"
          >
            Open #{{ savedReturn.id }} ·
            {{ formatDate(savedReturn.period_start) }} –
            {{ formatDate(savedReturn.period_end) }} · {{ savedReturn.status }}
          </button>
        </div>
      </div>
    </form-box>

    <ReferenceTabNavigation
      id-prefix="vat-returns"
      label="VAT return sections"
      :tabs="tabs"
      :active-tab="activeTab"
      @select="selectTab"
    />
    <div
      id="vat-returns-tab-panel"
      role="tabpanel"
      :aria-label="activeTabLabel"
      :aria-labelledby="`vat-returns-tab-${activeTab}`"
      tabindex="0"
      class="space-y-6"
    >
      <VatReturnStatistics
        v-if="activeTab === 'statistics'"
        :initial-year="quarterYear"
        @open="openStatisticsReturn"
      />
      <div
        v-show="activeTab === 'overview'"
        data-vat-section="overview"
        class="space-y-6"
      >
        <form-box header="Return Overview">
          <div
            v-if="vatReturn"
            class="grid gap-3 md:grid-cols-4 lg:grid-cols-8"
          >
            <div
              class="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900"
            >
              <div class="text-xs uppercase text-slate-500">Return</div>
              <div class="font-semibold">{{ vatReturn.label || "Draft" }}</div>
            </div>
            <div
              class="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900"
            >
              <div class="text-xs uppercase text-slate-500">Needs review</div>
              <div class="font-semibold">
                {{ summary.unmatched_transactions ?? 0 }}
              </div>
            </div>
            <div
              class="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900"
            >
              <div class="text-xs uppercase text-slate-500">Resolved out</div>
              <div class="font-semibold">
                {{ summary.resolved_outgoing_transactions ?? 0 }}
              </div>
            </div>
            <div
              class="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900"
            >
              <div class="text-xs uppercase text-slate-500">Invoices</div>
              <div class="font-semibold">
                {{ summary.purchase_invoices ?? 0 }}
              </div>
            </div>
            <div
              class="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900"
            >
              <div class="text-xs uppercase text-slate-500">
                Unmatched evidence
              </div>
              <div class="font-semibold">
                {{ summary.unmatched_purchase_invoices ?? 0 }}
              </div>
            </div>
            <div
              class="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900"
            >
              <div class="text-xs uppercase text-slate-500">VAT items</div>
              <div class="font-semibold">{{ summary.vat_items ?? 0 }}</div>
            </div>
            <div
              class="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900"
            >
              <div class="text-xs uppercase text-slate-500">
                Input VAT including estimates
              </div>
              <div class="font-semibold">
                {{ formatCurrency(summary.input_vat ?? 0) }}
              </div>
              <div class="text-xs text-slate-500">
                {{ formatCurrency(summary.estimated_input_vat ?? 0) }} awaiting
                invoice evidence
              </div>
            </div>
            <div
              class="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900"
            >
              <div class="text-xs uppercase text-slate-500">Output VAT</div>
              <div class="font-semibold">
                {{ formatCurrency(summary.output_vat ?? 0) }}
              </div>
            </div>
            <div
              class="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900"
            >
              <div class="text-xs uppercase text-slate-500">Income</div>
              <div class="font-semibold">
                {{ formatCurrency(summary.income_gross ?? 0) }}
              </div>
            </div>
          </div>
          <p v-else class="text-sm text-slate-500 dark:text-slate-400">
            Select a period and load the workspace to view the return summary.
          </p>
        </form-box>
      </div>
      <div
        v-show="activeTab === 'outgoing'"
        data-vat-section="outgoing"
        class="space-y-6"
      >
        <div class="flex flex-wrap items-center justify-between gap-3">
          <p class="text-sm text-slate-600 dark:text-slate-300">
            {{ summary.earlier_outgoing_transactions ?? 0 }} earlier
            transactions included on this return.
          </p>
          <button
            type="button"
            class="btn"
            :disabled="
              !vatReturn ||
              loading ||
              ['locked', 'submitted'].includes(vatReturn.status) ||
              vatReturn.filed_import
            "
            @click="showEarlierOutgoing = true"
          >
            Add earlier transactions
          </button>
        </div>
        <form-box
          header="Outgoing Transactions Needing Evidence"
          :collapsible="true"
        >
          <div
            class="mb-4 grid gap-3 xl:grid-cols-[auto_minmax(220px,1fr)_minmax(260px,1fr)_auto_minmax(180px,1fr)_auto] xl:items-end"
          >
            <div class="flex flex-wrap gap-2">
              <button
                class="btn text-xs"
                :disabled="!filteredUnmatchedTransactions.length"
                @click.prevent="toggleVisibleTransactions"
              >
                {{
                  allVisibleTransactionsSelected
                    ? "Deselect page"
                    : "Select page"
                }}
              </button>
              <button
                class="btn text-xs"
                :disabled="!selectedTransactionCount"
                @click.prevent="clearSelectedTransactions"
              >
                Clear
              </button>
              <label
                class="inline-flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300"
              >
                <input
                  v-model="showLinkedSupplierTransactions"
                  type="checkbox"
                  class="h-4 w-4 rounded border-slate-300"
                />
                Show linked suppliers
              </label>
            </div>
            <div>
              <label class="text-xs uppercase text-slate-500"
                >Transaction description</label
              >
              <input
                v-model="outgoingTransactionSearch"
                type="search"
                class="input mt-1 w-full dark:border-slate-300 dark:bg-slate-800"
                placeholder="Search descriptions"
              />
            </div>
            <div>
              <label class="text-xs uppercase text-slate-500"
                >Supplier to assign</label
              >
              <div
                v-if="bulkSupplier"
                class="mt-1 flex items-center justify-between rounded border border-slate-200 px-3 py-2 text-sm dark:border-slate-700"
              >
                <span>{{ bulkSupplier.company }}</span>
                <button
                  class="text-xs text-rose-600"
                  @click.prevent="clearBulkSupplier"
                >
                  Remove
                </button>
              </div>
              <SearchableInput
                v-else
                :key="`bulk-supplier-${bulkSupplierInputKey}`"
                url="/api/supplier/search?query="
                :output="supplierOutput"
                :can-add="canCreateSuppliers"
                :add-only-when-empty="true"
                add-label="Add supplier"
                placeholder="Search suppliers"
                @selected="selectBulkSupplier"
                @empty="openSupplierCreateModal($event, 'bulk')"
              />
            </div>
            <button
              class="btn accent"
              :disabled="
                bulkAssigning || !bulkSupplier || !selectedTransactionCount
              "
              @click.prevent="bulkAssignSupplier"
            >
              {{
                bulkAssigning
                  ? "Assigning…"
                  : `Assign ${selectedTransactionCount}`
              }}
            </button>
            <div>
              <label class="text-xs uppercase text-slate-500"
                >Review selected</label
              >
              <select v-model="bulkReviewStatus" class="input mt-1 w-full">
                <option value="">Choose outcome</option>
                <option
                  v-for="option in outgoingReviewOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
            <button
              class="btn"
              :disabled="
                reviewingOutgoing ||
                !bulkReviewStatus ||
                !selectedTransactionCount
              "
              @click.prevent="bulkReviewOutgoingTransactions"
            >
              {{
                reviewingOutgoing
                  ? "Marking…"
                  : `Mark ${selectedTransactionCount}`
              }}
            </button>
          </div>
          <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
            <p class="text-xs text-slate-500 dark:text-slate-400" role="status">
              Showing {{ outgoingPageStart }}–{{ outgoingPageEnd }} of
              {{ filteredUnmatchedTransactions.length }} matching transactions
              ({{ unmatchedTransactions.length }} total) ·
              {{ selectedTransactionCount }} selected across pages
            </p>
            <label
              class="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300"
            >
              Rows per page
              <select
                v-model.number="outgoingPerPage"
                aria-label="Outgoing transactions per page"
                class="input rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              >
                <option
                  v-for="size in outgoingPerPageOptions"
                  :key="size"
                  :value="size"
                >
                  {{ size }} / page
                </option>
              </select>
            </label>
          </div>
          <div class="overflow-x-auto">
            <table class="min-w-full text-sm">
              <thead>
                <tr class="text-left text-xs uppercase text-slate-500">
                  <th class="px-3 py-2"></th>
                  <th class="px-3 py-2">Date</th>
                  <th class="px-3 py-2">Description</th>
                  <th class="px-3 py-2">Supplier</th>
                  <th
                    class="px-3 py-2 text-right"
                    scope="col"
                    :aria-sort="
                      outgoingPaidOutSortActive
                        ? outgoingPaidOutSortDirection === 'ASC'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    "
                  >
                    <button
                      type="button"
                      class="inline-flex items-center gap-1 text-xs uppercase text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                      @click.prevent="toggleOutgoingPaidOutSort"
                    >
                      <span>Paid out</span>
                      <span aria-hidden="true">
                        {{ outgoingPaidOutSortIndicator }}
                      </span>
                    </button>
                  </th>
                  <th class="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="transaction in paginatedOutgoingTransactions"
                  data-outgoing-row
                  :key="transaction.id"
                  class="border-t border-slate-200 dark:border-slate-700"
                >
                  <td class="px-3 py-2">
                    <input
                      v-model="selectedTransactionIds"
                      type="checkbox"
                      :value="transaction.id"
                    />
                  </td>
                  <td class="px-3 py-2 whitespace-nowrap">
                    {{ formatDate(transaction.transaction_date) }}
                  </td>
                  <td class="px-3 py-2">
                    {{ transaction.description }}
                    <span
                      v-if="
                        transaction.transaction_date < vatReturn?.period_start
                      "
                      class="ml-2 rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-900 dark:bg-amber-900/40 dark:text-amber-200"
                      >Earlier period</span
                    >
                  </td>
                  <td class="px-3 py-2">
                    {{ transaction.supplier?.company || "Not linked" }}
                  </td>
                  <td class="px-3 py-2 text-right">
                    {{ formatCurrency(transaction.paid_out) }}
                  </td>
                  <td class="px-3 py-2">
                    <div class="flex justify-end gap-2">
                      <button
                        v-if="
                          transaction.transaction_date < vatReturn?.period_start
                        "
                        type="button"
                        class="btn text-xs"
                        :disabled="
                          removingEarlierTransactionId !== null ||
                          ['locked', 'submitted'].includes(vatReturn.status)
                        "
                        @click="removeEarlierTransaction(transaction)"
                      >
                        {{
                          removingEarlierTransactionId === transaction.id
                            ? "Removing…"
                            : "Remove from return"
                        }}
                      </button>
                      <select
                        class="input w-36 text-xs"
                        :disabled="reviewingOutgoing"
                        @change="
                          markOutgoingTransactionReviewed(
                            transaction,
                            $event.target.value,
                          );
                          $event.target.value = '';
                        "
                      >
                        <option value="">Resolve</option>
                        <option
                          v-for="option in outgoingReviewOptions"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                      <button
                        class="btn text-xs"
                        @click.prevent="
                          openTransactionSupplierModal(transaction)
                        "
                      >
                        Supplier
                      </button>
                    </div>
                  </td>
                </tr>
                <tr v-if="!filteredUnmatchedTransactions.length">
                  <td colspan="6" class="px-3 py-4 text-slate-500">
                    No outgoing transactions match the current period and
                    filters.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <nav
            aria-label="Outgoing transactions pagination"
            class="mt-4 flex flex-wrap items-center justify-between gap-3"
          >
            <span class="text-sm text-slate-600 dark:text-slate-300"
              >Page {{ outgoingPage }} of {{ outgoingPageCount }}</span
            >
            <PrevNextButtons
              :show-prev="outgoingPage > 1"
              :show-next="outgoingPage < outgoingPageCount"
              @prev="outgoingPage = Math.max(1, outgoingPage - 1)"
              @next="
                outgoingPage = Math.min(outgoingPageCount, outgoingPage + 1)
              "
            />
          </nav>
        </form-box>
        <form-box
          header="Resolved Outgoing Transactions"
          :collapsible="true"
          :collapsed="true"
        >
          <div class="mb-2 text-xs text-slate-500">
            {{ resolvedOutgoingTransactions.length }} transactions hidden from
            the active evidence queue
          </div>
          <div class="overflow-x-auto">
            <table class="min-w-full text-sm">
              <thead>
                <tr class="text-left text-xs uppercase text-slate-500">
                  <th class="px-3 py-2">Date</th>
                  <th class="px-3 py-2">Description</th>
                  <th class="px-3 py-2">Supplier</th>
                  <th class="px-3 py-2">Resolution</th>
                  <th class="px-3 py-2">Reviewed</th>
                  <th class="px-3 py-2 text-right">Paid out</th>
                  <th class="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="review in paginatedResolvedOutgoingTransactions"
                  data-resolved-outgoing-row
                  :key="review.id"
                  class="border-t border-slate-200 dark:border-slate-700"
                >
                  <td class="px-3 py-2 whitespace-nowrap">
                    {{ formatDate(review.transaction_date) }}
                  </td>
                  <td class="px-3 py-2">
                    <div>{{ review.description }}</div>
                    <div v-if="review.notes" class="text-xs text-slate-500">
                      {{ review.notes }}
                    </div>
                  </td>
                  <td class="px-3 py-2">
                    {{ review.supplier?.company || "Not linked" }}
                  </td>
                  <td class="px-3 py-2">
                    {{
                      review.status_label || reviewStatusLabel(review.status)
                    }}
                  </td>
                  <td class="px-3 py-2">
                    <div>{{ review.reviewed_by?.name || "Unknown" }}</div>
                    <div class="text-xs text-slate-500">
                      {{ formatDate(review.reviewed_at) }}
                    </div>
                  </td>
                  <td class="px-3 py-2 text-right">
                    {{ formatCurrency(review.paid_out) }}
                  </td>
                  <td class="px-3 py-2 text-right">
                    <button
                      class="btn text-xs"
                      :disabled="reviewingOutgoing"
                      @click.prevent="reopenOutgoingTransaction(review)"
                    >
                      Reopen
                    </button>
                  </td>
                </tr>
                <tr v-if="!resolvedOutgoingTransactions.length">
                  <td colspan="7" class="px-3 py-4 text-slate-500">
                    No outgoing transactions have been resolved without
                    evidence.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <nav
            aria-label="Resolved outgoing transactions pagination"
            class="mt-4 flex flex-wrap items-center justify-between gap-3"
          >
            <span class="text-sm text-slate-600 dark:text-slate-300"
              >Page {{ resolvedOutgoingPage }} of
              {{ resolvedOutgoingPageCount }}</span
            >
            <PrevNextButtons
              :show-prev="resolvedOutgoingPage > 1"
              :show-next="resolvedOutgoingPage < resolvedOutgoingPageCount"
              @prev="
                resolvedOutgoingPage = Math.max(1, resolvedOutgoingPage - 1)
              "
              @next="
                resolvedOutgoingPage = Math.min(
                  resolvedOutgoingPageCount,
                  resolvedOutgoingPage + 1,
                )
              "
            />
          </nav>
        </form-box>
      </div>
      <div
        v-show="activeTab === 'evidence'"
        data-vat-section="evidence"
        class="space-y-6"
      >
        <form-box
          :header="
            activePurchaseInvoice
              ? 'Edit Purchase Invoice Evidence'
              : 'Upload Purchase Invoice Evidence'
          "
          :collapsible="true"
          :collapsed="!activePurchaseInvoice"
        >
          <div
            v-if="activePurchaseInvoice"
            class="mb-4 rounded border border-cyan-200 bg-cyan-50 px-3 py-2 text-sm text-cyan-900 dark:border-cyan-900 dark:bg-cyan-950/30 dark:text-cyan-100"
          >
            Editing evidence #{{ activePurchaseInvoice.id }} from
            {{ purchaseInvoiceSource(activePurchaseInvoice) }}.
            {{
              activePurchaseInvoice.has_file
                ? "The existing evidence file will be kept."
                : ""
            }}
          </div>
          <div class="grid gap-4 lg:grid-cols-3">
            <div class="space-y-2">
              <label class="text-xs uppercase text-slate-500">Supplier</label>
              <div
                v-if="selectedSupplier"
                class="flex items-center justify-between rounded border border-slate-200 px-3 py-2 text-sm dark:border-slate-700"
              >
                <span>{{ selectedSupplier.company }}</span>
                <button
                  class="text-xs text-rose-600"
                  @click.prevent="clearInvoiceSupplier"
                >
                  Remove
                </button>
              </div>
              <SearchableInput
                v-else
                :key="`invoice-supplier-${supplierInputKey}`"
                url="/api/supplier/search?query="
                :output="supplierOutput"
                placeholder="Search suppliers"
                @selected="selectInvoiceSupplier"
              />
            </div>
            <div class="space-y-2">
              <label class="text-xs uppercase text-slate-500"
                >Invoice date</label
              >
              <input
                v-model="invoiceForm.invoice_date"
                type="date"
                class="input w-full"
              />
            </div>
            <div class="space-y-2">
              <label class="text-xs uppercase text-slate-500"
                >Invoice number</label
              >
              <input
                v-model="invoiceForm.invoice_number"
                type="text"
                class="input w-full"
              />
            </div>
            <div class="space-y-2">
              <label class="text-xs uppercase text-slate-500">Due date</label>
              <input
                v-model="invoiceForm.due_date"
                type="date"
                class="input w-full"
              />
            </div>
            <div class="space-y-2">
              <label class="text-xs uppercase text-slate-500"
                >Description</label
              >
              <input
                v-model="invoiceForm.description"
                type="text"
                class="input w-full"
              />
            </div>
            <div class="space-y-2">
              <label class="text-xs uppercase text-slate-500">Gross</label>
              <input
                v-model="invoiceForm.gross_amount"
                type="number"
                step="0.01"
                min="0"
                class="input w-full"
              />
            </div>
            <div class="space-y-2">
              <label class="text-xs uppercase text-slate-500"
                >VAT treatment</label
              >
              <select v-model="invoiceForm.vat_treatment" class="input w-full">
                <option
                  v-for="option in vatTreatmentOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
            <div class="space-y-2">
              <label class="text-xs uppercase text-slate-500">Category</label>
              <input
                v-model="invoiceForm.category"
                type="text"
                class="input w-full"
                placeholder="Vehicles, IT, Medical Supp"
              />
            </div>
            <div class="space-y-2">
              <label class="text-xs uppercase text-slate-500">Analysis</label>
              <input
                v-model="invoiceForm.analysis"
                type="text"
                class="input w-full"
                placeholder="Maintenance, Sundries"
              />
            </div>
            <div class="space-y-2">
              <label class="text-xs uppercase text-slate-500"
                >Evidence file</label
              >
              <input
                v-if="!activePurchaseInvoice"
                type="file"
                class="input w-full"
                @change="handleInvoiceFile"
              />
              <a
                v-else-if="activePurchaseInvoice.has_file"
                class="btn text-xs inline-flex items-center gap-1"
                :href="downloadInvoiceUrl(activePurchaseInvoice)"
              >
                <ArrowDownTrayIcon class="icon" />
                Download
              </a>
              <a
                v-if="activePurchaseInvoice?.has_file"
                class="btn text-xs inline-flex items-center gap-1"
                :href="viewInvoiceUrl(activePurchaseInvoice)"
                target="_blank"
                rel="noopener"
              >
                <EyeIcon class="icon" />
                View
              </a>
              <div v-else class="text-sm text-slate-500">No evidence file</div>
            </div>
          </div>
          <div class="mt-4 space-y-2">
            <label class="text-xs uppercase text-slate-500">Notes</label>
            <textarea
              v-model="invoiceForm.notes"
              class="input min-h-[80px] w-full"
            ></textarea>
          </div>
          <div class="mt-4 flex justify-end gap-2">
            <button
              v-if="activePurchaseInvoice"
              class="btn"
              :disabled="savingInvoice"
              @click.prevent="cancelPurchaseInvoiceEdit"
            >
              Cancel
            </button>
            <button
              class="btn accent"
              :disabled="savingInvoice"
              @click.prevent="saveInvoice"
            >
              {{
                savingInvoice
                  ? "Saving…"
                  : activePurchaseInvoice
                    ? "Save Changes"
                    : "Save Invoice"
              }}
            </button>
          </div>
        </form-box>
        <form-box header="Match Evidence To Bank Transactions">
          <div
            class="grid gap-3 lg:grid-cols-[1fr_1fr_160px_auto] lg:items-end"
          >
            <div class="space-y-1">
              <label class="text-xs uppercase text-slate-500"
                >Invoice evidence</label
              >
              <select
                v-model="matchForm.purchase_invoice_id"
                class="input w-full"
              >
                <option value="">Select invoice</option>
                <option
                  v-for="invoice in matchablePurchaseInvoices"
                  :key="invoice.id"
                  :value="invoice.id"
                >
                  {{ purchaseInvoiceLabel(invoice) }}
                </option>
              </select>
            </div>
            <div class="space-y-1">
              <label class="text-xs uppercase text-slate-500"
                >Bank transaction</label
              >
              <select
                v-model="matchForm.bank_transaction_id"
                class="input w-full"
              >
                <option value="">Select transaction</option>
                <option
                  v-for="transaction in filteredUnmatchedTransactions"
                  :key="transaction.id"
                  :value="transaction.id"
                >
                  {{ formatDate(transaction.transaction_date) }} ·
                  {{ transaction.description }} ·
                  {{ formatCurrency(transaction.paid_out) }}
                </option>
              </select>
            </div>
            <div class="space-y-1">
              <label class="text-xs uppercase text-slate-500">Allocated</label>
              <input
                v-model="matchForm.allocated_amount"
                type="number"
                step="0.01"
                min="0"
                class="input w-full"
                placeholder="Auto"
              />
            </div>
            <button
              class="btn accent px-3"
              :disabled="matching"
              @click.prevent="linkInvoiceToTransaction"
            >
              <LinkIcon class="icon" />
            </button>
          </div>
        </form-box>
        <form-box
          header="Unmatched Purchase Invoice Evidence"
          :collapsible="true"
        >
          <div class="mb-2 text-xs text-slate-500">
            Evidence here has been filed but is not linked to a bank transaction
            yet.
          </div>
          <div class="overflow-x-auto">
            <table class="min-w-full text-sm">
              <thead>
                <tr class="text-left text-xs uppercase text-slate-500">
                  <th class="px-3 py-2">Filed</th>
                  <th class="px-3 py-2">Supplier</th>
                  <th class="px-3 py-2">Invoice</th>
                  <th class="px-3 py-2">Source</th>
                  <th class="px-3 py-2">Bank transaction</th>
                  <th class="px-3 py-2 text-right">Gross</th>
                  <th class="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="invoice in unmatchedPurchaseInvoices"
                  :key="invoice.id"
                  class="border-t border-slate-200 dark:border-slate-700"
                >
                  <td class="px-3 py-2 whitespace-nowrap">
                    {{ formatDate(invoice.created_at) }}
                  </td>
                  <td class="px-3 py-2">
                    <div>{{ invoice.supplier?.company || "No supplier" }}</div>
                    <div
                      v-if="evidenceNeedsReview(invoice)"
                      class="text-xs text-amber-700 dark:text-amber-300"
                    >
                      Needs details
                    </div>
                    <div
                      v-if="invoiceExtractionPending(invoice)"
                      class="text-xs text-cyan-700 dark:text-cyan-300"
                    >
                      Rescanning attachment…
                    </div>
                    <div
                      v-else-if="invoice.extraction_status === 'failed'"
                      class="text-xs text-rose-700 dark:text-rose-300"
                      :title="invoice.extraction_error"
                    >
                      Previous scan failed
                    </div>
                  </td>
                  <td class="px-3 py-2">
                    <div>
                      {{ invoice.invoice_number || invoice.description || "—" }}
                    </div>
                    <div class="text-xs text-slate-500">
                      {{ formatDate(invoice.invoice_date) }}
                    </div>
                  </td>
                  <td class="px-3 py-2">
                    {{ purchaseInvoiceSource(invoice) }}
                  </td>
                  <td class="px-3 py-2">
                    <div
                      v-for="label in bankTransactionLabels(invoice)"
                      :key="label"
                    >
                      {{ label }}
                    </div>
                  </td>
                  <td class="px-3 py-2 text-right">
                    {{ formatCurrency(invoice.gross_amount) }}
                  </td>
                  <td class="px-3 py-2">
                    <div class="flex justify-end gap-2">
                      <a
                        v-if="invoice.has_file"
                        class="btn text-xs inline-flex items-center gap-1"
                        :href="viewInvoiceUrl(invoice)"
                        target="_blank"
                        rel="noopener"
                        title="View evidence"
                      >
                        <EyeIcon class="icon" />
                      </a>
                      <a
                        v-if="invoice.has_file"
                        class="btn text-xs inline-flex items-center gap-1"
                        :href="downloadInvoiceUrl(invoice)"
                        title="Download evidence"
                      >
                        <ArrowDownTrayIcon class="icon" />
                      </a>
                      <button
                        v-if="invoice.has_file"
                        class="btn text-xs inline-flex items-center gap-1"
                        :disabled="
                          invoiceExtractionPending(invoice) ||
                          invoiceIsRescanning(invoice)
                        "
                        title="Rescan evidence with the best invoice-reading model"
                        @click.prevent="rescanPurchaseInvoice(invoice)"
                      >
                        <ArrowPathIcon
                          class="icon"
                          :class="{
                            'animate-spin':
                              invoiceExtractionPending(invoice) ||
                              invoiceIsRescanning(invoice),
                          }"
                        />
                        {{
                          invoiceExtractionPending(invoice) ||
                          invoiceIsRescanning(invoice)
                            ? "Rescanning…"
                            : "Rescan"
                        }}
                      </button>
                      <button
                        class="btn text-xs"
                        @click.prevent="editPurchaseInvoice(invoice)"
                      >
                        Edit
                      </button>
                      <button
                        class="btn text-xs"
                        :disabled="Number(invoice.gross_amount ?? 0) <= 0"
                        @click.prevent="selectPurchaseInvoiceForMatch(invoice)"
                      >
                        Match
                      </button>
                      <button
                        class="btn danger text-xs inline-flex items-center gap-1"
                        :disabled="deletingInvoiceId !== null"
                        title="Delete this unmatched evidence"
                        @click.prevent="deletePurchaseInvoice(invoice)"
                      >
                        <TrashIcon class="icon" />
                        {{
                          deletingInvoiceId === Number(invoice.id)
                            ? "Deleting…"
                            : "Delete"
                        }}
                      </button>
                    </div>
                  </td>
                </tr>
                <tr v-if="!unmatchedPurchaseInvoices.length">
                  <td colspan="7" class="px-3 py-4 text-slate-500">
                    No unmatched purchase invoice evidence.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </form-box>
        <form-box header="Purchase Invoice Evidence" :collapsible="true">
          <div class="overflow-x-auto">
            <table class="min-w-full text-sm">
              <thead>
                <tr class="text-left text-xs uppercase text-slate-500">
                  <th class="px-3 py-2">Date</th>
                  <th class="px-3 py-2">Supplier</th>
                  <th class="px-3 py-2">Invoice</th>
                  <th class="px-3 py-2">Bank transaction</th>
                  <th class="px-3 py-2">VAT</th>
                  <th class="px-3 py-2 text-right">Gross</th>
                  <th class="px-3 py-2 text-right">Outstanding</th>
                  <th class="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="invoice in purchaseInvoices"
                  :key="invoice.id"
                  class="border-t border-slate-200 dark:border-slate-700"
                >
                  <td class="px-3 py-2 whitespace-nowrap">
                    {{ formatDate(invoice.invoice_date) }}
                  </td>
                  <td class="px-3 py-2">
                    {{ invoice.supplier?.company || "No supplier" }}
                  </td>
                  <td class="px-3 py-2">
                    {{ invoice.invoice_number || invoice.description || "—" }}
                  </td>
                  <td class="px-3 py-2">
                    <div
                      v-for="label in bankTransactionLabels(invoice)"
                      :key="label"
                    >
                      {{ label }}
                    </div>
                  </td>
                  <td class="px-3 py-2">{{ invoice.vat_treatment }}</td>
                  <td class="px-3 py-2 text-right">
                    {{ formatCurrency(invoice.gross_amount) }}
                  </td>
                  <td class="px-3 py-2 text-right">
                    {{
                      formatCurrency(
                        invoice.outstanding_amount ?? invoice.gross_amount,
                      )
                    }}
                  </td>
                  <td class="px-3 py-2 text-right">
                    <a
                      v-if="invoice.has_file"
                      class="btn text-xs inline-flex items-center gap-1"
                      :href="viewInvoiceUrl(invoice)"
                      target="_blank"
                      rel="noopener"
                      title="View evidence"
                    >
                      <EyeIcon class="icon" />
                    </a>
                    <a
                      v-if="invoice.has_file"
                      class="btn text-xs inline-flex items-center gap-1"
                      :href="downloadInvoiceUrl(invoice)"
                      title="Download evidence"
                    >
                      <ArrowDownTrayIcon class="icon" />
                    </a>
                    <button
                      class="btn text-xs ml-2"
                      @click.prevent="editPurchaseInvoice(invoice)"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
                <tr v-if="!purchaseInvoices.length">
                  <td colspan="8" class="px-3 py-4 text-slate-500">
                    No purchase invoice evidence for this period yet.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </form-box>
      </div>
      <div
        v-show="activeTab === 'vat-sheet'"
        data-vat-section="vat-sheet"
        class="space-y-6"
      >
        <form-box header="VAT Sheet Items" :collapsible="true">
          <div class="overflow-x-auto">
            <table class="min-w-full text-sm">
              <thead>
                <tr class="text-left text-xs uppercase text-slate-500">
                  <th class="px-3 py-2">Date</th>
                  <th class="px-3 py-2">Transaction</th>
                  <th class="px-3 py-2">Category</th>
                  <th class="px-3 py-2">Analysis</th>
                  <th class="px-3 py-2">Invoice</th>
                  <th class="px-3 py-2">Evidence</th>
                  <th class="px-3 py-2 text-right">Gross</th>
                  <th class="px-3 py-2 text-right">Net</th>
                  <th class="px-3 py-2 text-right">VAT</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in vatItems"
                  :key="`${item.purchase_invoice_id}-${item.bank_transaction_id}`"
                  class="border-t border-slate-200 dark:border-slate-700"
                >
                  <td class="px-3 py-2 whitespace-nowrap">
                    {{ formatDate(item.date) }}
                  </td>
                  <td class="px-3 py-2">{{ item.transaction }}</td>
                  <td class="px-3 py-2">{{ item.category || "—" }}</td>
                  <td class="px-3 py-2">{{ item.analysis || "—" }}</td>
                  <td class="px-3 py-2">
                    {{
                      item.invoice_number ||
                      (item.is_inferred ? "Supplier default" : "—")
                    }}
                  </td>
                  <td class="px-3 py-2">
                    {{
                      item.is_inferred
                        ? "Invoice required"
                        : item.has_file
                          ? "Uploaded"
                          : "No file"
                    }}
                  </td>
                  <td class="px-3 py-2 text-right">
                    {{ formatCurrency(item.gross_amount) }}
                  </td>
                  <td class="px-3 py-2 text-right">
                    {{ formatCurrency(item.net_amount) }}
                  </td>
                  <td class="px-3 py-2 text-right">
                    {{ formatCurrency(item.vat_amount) }}
                  </td>
                </tr>
                <tr v-if="!vatItems.length">
                  <td colspan="9" class="px-3 py-4 text-slate-500">
                    No matched VAT items yet.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </form-box>
      </div>
      <div
        v-show="activeTab === 'income'"
        data-vat-section="income"
        class="space-y-6"
      >
        <form-box header="Income Transactions" :collapsible="true">
          <div class="mb-3 flex flex-wrap items-end justify-between gap-3">
            <div class="text-xs text-slate-500">
              Showing {{ filteredIncomeTransactions.length }} of
              {{ incomeTransactions.length }} transactions ·
              {{ summary.matched_income_transactions ?? 0 }} matched ·
              {{ summary.resolved_income_transactions ?? 0 }} resolved without
              an invoice
            </div>
            <label class="flex flex-col gap-1 text-xs uppercase text-slate-500">
              Invoice status
              <select
                v-model="incomeInvoiceFilter"
                class="input min-w-44 text-sm"
              >
                <option value="all">All transactions</option>
                <option value="unmatched">Unmatched only</option>
              </select>
            </label>
          </div>
          <div class="overflow-x-auto">
            <table class="min-w-full text-sm">
              <thead>
                <tr class="text-left text-xs uppercase text-slate-500">
                  <th class="px-3 py-2">Date</th>
                  <th class="px-3 py-2">Description</th>
                  <th class="px-3 py-2">Matched invoice</th>
                  <th class="px-3 py-2">Suggested invoice</th>
                  <th class="px-3 py-2">Review / notes</th>
                  <th class="px-3 py-2 text-right">Paid in</th>
                  <th class="px-3 py-2 text-right">VAT</th>
                  <th class="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="transaction in filteredIncomeTransactions"
                  :key="transaction.id"
                  data-income-transaction-row
                  class="cursor-pointer border-t border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
                  @click="openIncomeInvoiceFromRow($event, transaction)"
                >
                  <td class="px-3 py-2 whitespace-nowrap">
                    {{ formatDate(transaction.transaction_date) }}
                  </td>
                  <td class="px-3 py-2">
                    <button
                      type="button"
                      class="text-left text-sky-700 underline decoration-dotted underline-offset-4 hover:decoration-solid focus-visible:outline focus-visible:outline-2 dark:text-sky-300"
                      :aria-label="`Search invoices for ${transaction.description}`"
                      :disabled="savingIncomeInvoice"
                      @click.stop="openIncomeInvoiceModal(transaction)"
                    >
                      {{ transaction.description }}
                    </button>
                  </td>
                  <td class="px-3 py-2">
                    <div
                      v-if="matchedIncomeInvoice(transaction)"
                      class="space-y-2"
                    >
                      <div
                        v-for="invoice in transaction.invoices"
                        :key="invoice.id"
                      >
                        {{ invoiceLabel(invoice) }} ·
                        {{ formatCurrency(invoice.allocated_amount) }}
                        <button
                          class="ml-2 text-xs text-rose-600"
                          :disabled="savingIncomeInvoice"
                          @click.prevent="
                            clearIncomeInvoiceMatch(transaction, invoice)
                          "
                        >
                          Remove
                        </button>
                      </div>
                      <div
                        v-if="transaction.unallocated_amount > 0"
                        class="text-amber-600"
                      >
                        {{ formatCurrency(transaction.unallocated_amount) }}
                        left to allocate
                      </div>
                    </div>
                    <span v-else-if="transaction.review" class="text-slate-500">
                      Not required
                    </span>
                    <span v-else class="text-slate-500">Not matched</span>
                  </td>
                  <td class="px-3 py-2">
                    <span v-if="suggestedIncomeInvoice(transaction)">
                      {{ invoiceLabel(suggestedIncomeInvoice(transaction)) }}
                      <span class="text-xs text-slate-500">
                        score
                        {{ suggestedIncomeInvoice(transaction).match_score }}
                      </span>
                    </span>
                    <span v-else class="text-slate-500">—</span>
                  </td>
                  <td class="px-3 py-2">
                    <div v-if="transaction.review" class="font-medium">
                      {{ transaction.review.status_label }}
                    </div>
                    <div
                      v-if="transaction.notes"
                      class="max-w-xs whitespace-pre-wrap text-xs text-slate-500"
                    >
                      {{ transaction.notes }}
                    </div>
                    <span
                      v-if="!transaction.review && !transaction.notes"
                      class="text-slate-500"
                    >
                      —
                    </span>
                  </td>
                  <td class="px-3 py-2 text-right">
                    {{ formatCurrency(transaction.paid_in) }}
                  </td>
                  <td class="px-3 py-2 text-right">
                    {{
                      formatCurrency(
                        transaction.invoices?.length
                          ? transaction.invoices.reduce(
                              (sum, invoice) =>
                                sum +
                                Math.round(
                                  (invoice.vat *
                                    (invoice.allocated_amount ??
                                      invoice.total_amount)) /
                                    invoice.total_amount,
                                ),
                              0,
                            )
                          : (suggestedIncomeInvoice(transaction)?.vat ?? 0),
                      )
                    }}
                  </td>
                  <td class="px-3 py-2 text-right">
                    <div class="flex justify-end gap-2">
                      <button
                        v-if="
                          !matchedIncomeInvoice(transaction) &&
                          !transaction.review &&
                          suggestedIncomeInvoice(transaction)
                        "
                        class="btn text-xs"
                        :disabled="savingIncomeInvoice"
                        @click.prevent="
                          applySuggestedIncomeInvoice(transaction)
                        "
                      >
                        Use
                      </button>
                      <button
                        v-if="!transaction.review"
                        class="btn text-xs"
                        :disabled="savingIncomeInvoice"
                        @click.prevent="openIncomeInvoiceModal(transaction)"
                      >
                        {{
                          matchedIncomeInvoice(transaction)
                            ? "Edit invoices"
                            : "Match"
                        }}
                      </button>
                      <button
                        class="btn text-xs"
                        :disabled="savingIncomeReview"
                        @click.prevent="openIncomeReviewModal(transaction)"
                      >
                        {{ incomeReviewButtonLabel(transaction) }}
                      </button>
                    </div>
                  </td>
                </tr>
                <tr v-if="!filteredIncomeTransactions.length">
                  <td colspan="8" class="px-3 py-4 text-slate-500">
                    No income transactions match the current filters.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </form-box>
        <form-box
          v-if="stripeIncomeTransactions.length"
          header="Stripe Customer Payments"
          :collapsible="true"
        >
          <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
            <p class="text-xs text-slate-500">
              These gross customer payments replace their net Stripe payout bank
              credits for VAT. Fees remain separate from sales and output VAT.
            </p>
            <router-link
              v-if="auth.hasRole('super')"
              to="/super/stripe-invoices"
              class="btn text-xs"
            >
              Open Stripe reconciliation
            </router-link>
          </div>
          <div class="overflow-x-auto">
            <table class="min-w-full text-sm">
              <thead>
                <tr class="text-left text-xs uppercase text-slate-500">
                  <th class="px-3 py-2">Payment date</th>
                  <th class="px-3 py-2">Customer / description</th>
                  <th class="px-3 py-2">Neptune invoice</th>
                  <th class="px-3 py-2 text-right">Gross</th>
                  <th class="px-3 py-2 text-right">Fee</th>
                  <th class="px-3 py-2 text-right">VAT</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="transaction in stripeIncomeTransactions"
                  :key="transaction.id"
                  class="border-t border-slate-200 dark:border-slate-700"
                >
                  <td class="whitespace-nowrap px-3 py-2">
                    {{ formatDate(transaction.paid_at) }}
                  </td>
                  <td class="px-3 py-2">
                    <div class="font-medium">
                      {{
                        transaction.customer_name ||
                        transaction.customer_email ||
                        "Stripe customer"
                      }}
                    </div>
                    <div class="max-w-md text-xs text-slate-500">
                      {{ transaction.description }}
                    </div>
                  </td>
                  <td class="px-3 py-2">
                    <div v-if="transaction.invoices?.length" class="space-y-1">
                      <div
                        v-for="invoice in transaction.invoices"
                        :key="invoice.id"
                      >
                        <router-link
                          :to="`/invoice/${invoice.id}`"
                          class="font-semibold text-cyan-700 hover:underline dark:text-cyan-300"
                        >
                          {{ invoiceLabel(invoice) }}
                        </router-link>
                        <div class="text-xs text-slate-500">
                          {{ formatCurrency(invoice.allocated_gross_amount) }}
                          gross ·
                          {{ formatCurrency(invoice.allocated_vat_amount) }} VAT
                        </div>
                      </div>
                    </div>
                    <span
                      v-if="transaction.allocation_status === 'unmatched'"
                      class="font-medium text-amber-600"
                    >
                      Needs invoice allocation
                    </span>
                    <span
                      v-else-if="transaction.allocation_status === 'partial'"
                      class="font-medium text-amber-600"
                    >
                      {{ formatCurrency(transaction.remaining_gross_amount) }}
                      still unallocated
                    </span>
                  </td>
                  <td class="px-3 py-2 text-right">
                    {{ formatCurrency(transaction.gross_amount) }}
                  </td>
                  <td class="px-3 py-2 text-right text-rose-600">
                    {{ formatCurrency(transaction.fee_amount) }}
                  </td>
                  <td class="px-3 py-2 text-right">
                    {{
                      transaction.invoices?.length
                        ? formatCurrency(transaction.vat_amount ?? 0)
                        : "Review"
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </form-box>
      </div>
    </div>
  </div>

  <div
    v-if="activeIncomeTransaction && vatReturn"
    class="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/70 px-4 py-8"
  >
    <div
      class="max-h-full w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl dark:bg-slate-800"
    >
      <div class="flex items-start justify-between gap-3">
        <div>
          <h2 class="text-lg font-semibold">Match Income To Invoices</h2>
          <p class="mt-1 text-sm text-slate-500">
            {{ activeIncomeTransaction.description }} ·
            {{ formatCurrency(activeIncomeTransaction.paid_in) }}
          </p>
        </div>
        <button class="btn" @click.prevent="closeIncomeInvoiceModal">
          Close
        </button>
      </div>

      <div class="mt-5 space-y-4">
        <div
          v-if="suggestedIncomeInvoice(activeIncomeTransaction)"
          class="rounded border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-700 dark:bg-slate-900"
        >
          <div class="text-xs uppercase text-slate-500">Suggested</div>
          <div class="mt-1 font-medium">
            {{ invoiceLabel(suggestedIncomeInvoice(activeIncomeTransaction)) }}
          </div>
          <div class="mt-1 text-xs text-slate-500">
            VAT
            {{
              formatCurrency(
                suggestedIncomeInvoice(activeIncomeTransaction).vat,
              )
            }}
          </div>
        </div>

        <div>
          <p class="mb-3 text-sm text-slate-500">
            Add one or more invoices and enter how much of this payment goes to
            each.
          </p>
          <div
            v-for="(row, index) in incomeAllocations"
            :key="row.invoice.id"
            class="mt-2 flex items-center gap-3 rounded border border-slate-200 px-3 py-2 text-sm dark:border-slate-700"
          >
            <span class="flex-1">{{ invoiceLabel(row.invoice) }}</span>
            <label class="text-xs"
              >Amount (£)
              <input
                v-model="row.amount"
                type="number"
                min="0.01"
                step="0.01"
                :max="row.invoice.total_amount / 100"
                :aria-label="`Amount for ${invoiceLabel(row.invoice)}`"
                class="input w-28"
                :disabled="savingIncomeInvoice"
              />
            </label>
            <button
              class="text-xs text-rose-600"
              :disabled="savingIncomeInvoice"
              @click.prevent="incomeAllocations.splice(index, 1)"
            >
              Remove
            </button>
          </div>
          <SearchableInput
            :key="`sales-invoice-${salesInvoiceInputKey}`"
            :url="`/api/vat-returns/${vatReturn.id}/sales-invoices/search?query=`"
            :output="invoiceLabel"
            placeholder="Search by customer, reference, purchase order"
            @selected="selectSalesInvoice"
          />
        </div>
      </div>

      <p
        class="mt-4 text-sm"
        :class="
          incomeAllocationRemaining < 0 ? 'text-rose-600' : 'text-slate-500'
        "
      >
        Allocated {{ formatCurrency(incomeAllocationTotal) }} ·
        {{ formatCurrency(incomeAllocationRemaining) }} left to allocate
      </p>
      <p
        v-if="incomeAllocationError"
        role="alert"
        class="mt-2 text-sm text-rose-600"
      >
        {{ incomeAllocationError }}
      </p>

      <div class="mt-6 flex justify-end gap-2">
        <button class="btn" @click.prevent="closeIncomeInvoiceModal">
          Cancel
        </button>
        <button
          class="btn accent"
          :disabled="savingIncomeInvoice || !validIncomeAllocations"
          @click.prevent="saveSelectedIncomeInvoice"
        >
          {{ savingIncomeInvoice ? "Saving…" : "Save Match" }}
        </button>
      </div>
    </div>
  </div>

  <div
    v-if="activeTransaction"
    class="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/70 px-4 py-8"
  >
    <div
      class="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl dark:bg-slate-800"
    >
      <div class="flex items-start justify-between gap-3">
        <div>
          <h2 class="text-lg font-semibold">Set Transaction Supplier</h2>
          <p class="mt-1 text-sm text-slate-500">
            {{ activeTransaction.description }}
          </p>
        </div>
        <button class="btn" @click.prevent="closeTransactionSupplierModal">
          Close
        </button>
      </div>
      <div class="mt-5 space-y-3">
        <div
          v-if="transactionSupplier"
          class="flex items-center justify-between rounded border border-slate-200 px-3 py-2 text-sm dark:border-slate-700"
        >
          <span>{{ transactionSupplier.company }}</span>
          <button
            class="text-xs text-rose-600"
            @click.prevent="transactionSupplier = null"
          >
            Remove
          </button>
        </div>
        <SearchableInput
          :key="`transaction-supplier-${transactionSupplierInputKey}`"
          url="/api/supplier/search?query="
          :output="supplierOutput"
          :can-add="canCreateSuppliers"
          :add-only-when-empty="true"
          add-label="Add supplier"
          placeholder="Search suppliers"
          @selected="selectTransactionSupplier"
          @empty="openSupplierCreateModal($event, 'transaction')"
        />
      </div>
      <div class="mt-6 flex justify-end gap-2">
        <button class="btn" @click.prevent="closeTransactionSupplierModal">
          Cancel
        </button>
        <button
          class="btn accent"
          :disabled="savingTransaction"
          @click.prevent="saveTransactionSupplier"
        >
          {{ savingTransaction ? "Saving…" : "Save Supplier" }}
        </button>
      </div>
    </div>
  </div>

  <HistoricalVatImportModal
    v-if="showHistoricalImport && vatReturn"
    :return-id="vatReturn.id"
    :period-start="vatReturn.period_start"
    :period-end="vatReturn.period_end"
    :existing-import-id="vatReturn.filed_import?.id ?? null"
    @cancel="showHistoricalImport = false"
    @imported="
      workspace = $event;
      showHistoricalImport = false;
      closureChecked = false;
      error = '';
      allocationConflicts = [];
      selectedInvoiceConflictKeys = [];
      pendingFinalisationStatus = null;
      syncSelectedTransactions();
    "
  />
  <EarlierOutgoingTransactionsModal
    v-if="showEarlierOutgoing && vatReturn"
    :key="vatReturn.id"
    :return-id="vatReturn.id"
    @cancel="showEarlierOutgoing = false"
    @included="useEarlierOutgoingTransactions"
  />

  <SupplierCreateModal
    v-if="supplierCreateTarget"
    :initial-company="supplierCreateCompany"
    @cancel="closeSupplierCreateModal"
    @created="useCreatedSupplier"
  />

  <IncomeTransactionReviewModal
    v-if="activeIncomeReviewTransaction"
    :transaction="activeIncomeReviewTransaction"
    :saving="savingIncomeReview"
    @cancel="closeIncomeReviewModal"
    @save="saveIncomeTransactionReview"
  />
</template>

<style scoped>
.vat-returns-workspace {
  container: reference-workspace / inline-size;
}
</style>
